import { useTranslation } from 'react-i18next';
import { X, ShoppingCart, Send, Package, FileDown, Truck } from 'lucide-react';
import { useSendPurchaseOrder } from '../../../api/mutations/purchasing.mutations';
import type { PurchaseOrder } from '../../../api/queries/purchasing.queries';

interface PODetailModalProps {
  po: PurchaseOrder;
  onClose: () => void;
  onRecordGRN: () => void;
  onRefresh: () => void;
}

export default function PODetailModal({ po, onClose, onRecordGRN, onRefresh }: PODetailModalProps) {
  const { t, i18n } = useTranslation();
  const sendPO = useSendPurchaseOrder();

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString(i18n.language === 'th' ? 'th-TH' : 'en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('th-TH', {
      style: 'currency',
      currency: 'THB',
      minimumFractionDigits: 2,
    }).format(amount);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'draft':
        return 'text-gray-600 bg-gray-50 border-gray-200';
      case 'sent':
        return 'text-blue-600 bg-blue-50 border-blue-200';
      case 'confirmed':
        return 'text-green-600 bg-green-50 border-green-200';
      case 'partially_received':
        return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'completed':
        return 'text-green-600 bg-green-50 border-green-200';
      case 'cancelled':
        return 'text-red-600 bg-red-50 border-red-200';
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const handleSendPO = async () => {
    if (!confirm(t('purchasing.confirmSend'))) return;
    try {
      await sendPO.mutateAsync(po._id);
      onRefresh();
    } catch (error) {
      console.error('Failed to send PO:', error);
    }
  };

  const canSend = po.status === 'draft';
  const canReceive = ['sent', 'confirmed', 'partially_received'].includes(po.status);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="relative w-full max-w-3xl max-h-[90vh] overflow-hidden rounded-lg bg-card flex flex-col">
        {/* Header */}
        <div className="border-b p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <ShoppingCart className="h-5 w-5 text-primary" />
            <div>
              <h2 className="text-xl font-bold">{po.poNumber}</h2>
              <p className="text-sm text-muted-foreground">{po.supplierId?.name}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className={`px-2 py-1 rounded border text-xs ${getStatusColor(po.status)}`}>
              {t(`purchasing.${po.status}`)}
            </span>
            {po.isConsolidated && (
              <span className="px-2 py-1 rounded bg-purple-50 text-purple-700 text-xs">
                {t('purchasing.consolidated')}
              </span>
            )}
            <button onClick={onClose} className="p-2 hover:bg-secondary rounded ml-2">
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="space-y-6">
            {/* Key Info */}
            <div className="grid grid-cols-3 gap-4 p-4 rounded-lg bg-secondary/50">
              <div>
                <p className="text-sm text-muted-foreground">{t('purchasing.createdDate')}</p>
                <p className="font-medium">{formatDate(po.createdAt)}</p>
              </div>
              {po.sentAt && (
                <div>
                  <p className="text-sm text-muted-foreground">{t('purchasing.sentDate')}</p>
                  <p className="font-medium">{formatDate(po.sentAt)}</p>
                </div>
              )}
              {po.expectedDeliveryDate && (
                <div>
                  <p className="text-sm text-muted-foreground">{t('purchasing.expectedDelivery')}</p>
                  <p className="font-medium">{formatDate(po.expectedDeliveryDate)}</p>
                </div>
              )}
            </div>

            {/* Delivery Info */}
            {po.deliveryBranchId && (
              <div className="flex items-center gap-3 p-4 rounded-lg border">
                <Truck className="h-5 w-5 text-muted-foreground" />
                <div>
                  <p className="text-sm text-muted-foreground">{t('purchasing.deliveryTo')}</p>
                  <p className="font-medium">{po.deliveryBranchId.name}</p>
                  {po.deliveryBranchId.address && (
                    <p className="text-sm text-muted-foreground">{po.deliveryBranchId.address}</p>
                  )}
                </div>
              </div>
            )}

            {/* Source PRs */}
            {po.consolidatedFrom && po.consolidatedFrom.length > 0 && (
              <div>
                <h3 className="text-sm font-medium mb-2">{t('purchasing.sourcePRs')}</h3>
                <div className="flex flex-wrap gap-2">
                  {po.consolidatedFrom.map((source) => (
                    <span
                      key={source.prId}
                      className="px-2 py-1 rounded bg-secondary text-sm"
                    >
                      {source.prNumber} ({source.branchId.name})
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Items Table */}
            <div>
              <h3 className="text-sm font-medium mb-3">{t('purchasing.items')}</h3>
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-secondary/50">
                    <tr>
                      <th className="text-left py-2 px-3">{t('purchasing.item')}</th>
                      <th className="text-right py-2 px-3">{t('purchasing.qty')}</th>
                      <th className="text-right py-2 px-3">{t('purchasing.received')}</th>
                      <th className="text-right py-2 px-3">{t('purchasing.unitPrice')}</th>
                      <th className="text-right py-2 px-3">{t('purchasing.total')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {po.items.map((item) => (
                      <tr key={item._id} className="border-t">
                        <td className="py-2 px-3">
                          {item.isFreeFormItem ? item.customItemName : item.ingredientId?.name}
                        </td>
                        <td className="py-2 px-3 text-right">{item.quantity} {item.unit}</td>
                        <td className="py-2 px-3 text-right">
                          {item.receivedQuantity > 0 ? (
                            <span className={item.receivedQuantity < item.quantity ? 'text-orange-600' : 'text-green-600'}>
                              {item.receivedQuantity} {item.unit}
                            </span>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </td>
                        <td className="py-2 px-3 text-right">{formatCurrency(item.unitPrice)}</td>
                        <td className="py-2 px-3 text-right font-medium">{formatCurrency(item.totalPrice)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-secondary/50">
                    <tr className="border-t">
                      <td colSpan={4} className="py-2 px-3 text-right">
                        {t('purchasing.subtotal')}:
                      </td>
                      <td className="py-2 px-3 text-right">{formatCurrency(po.subtotal)}</td>
                    </tr>
                    <tr>
                      <td colSpan={4} className="py-1 px-3 text-right text-muted-foreground">
                        {t('purchasing.tax')} ({po.taxRate}%):
                      </td>
                      <td className="py-1 px-3 text-right text-muted-foreground">{formatCurrency(po.tax)}</td>
                    </tr>
                    {po.shipping > 0 && (
                      <tr>
                        <td colSpan={4} className="py-1 px-3 text-right text-muted-foreground">
                          {t('purchasing.shipping')}:
                        </td>
                        <td className="py-1 px-3 text-right text-muted-foreground">{formatCurrency(po.shipping)}</td>
                      </tr>
                    )}
                    {po.discount > 0 && (
                      <tr>
                        <td colSpan={4} className="py-1 px-3 text-right text-green-600">
                          {t('purchasing.discount')}:
                        </td>
                        <td className="py-1 px-3 text-right text-green-600">-{formatCurrency(po.discount)}</td>
                      </tr>
                    )}
                    <tr className="border-t">
                      <td colSpan={4} className="py-2 px-3 text-right font-bold">
                        {t('purchasing.total')}:
                      </td>
                      <td className="py-2 px-3 text-right font-bold text-lg">{formatCurrency(po.total)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>

            {/* Split Deliveries */}
            {po.hasSplitDelivery && po.splitDeliveries && po.splitDeliveries.length > 0 && (
              <div>
                <h3 className="text-sm font-medium mb-3">{t('purchasing.splitDeliveries')}</h3>
                <div className="space-y-2">
                  {po.splitDeliveries.map((delivery) => (
                    <div key={delivery._id} className="flex items-center justify-between p-3 rounded-lg border">
                      <div>
                        <p className="font-medium">{formatDate(delivery.expectedDate)}</p>
                        <p className="text-sm text-muted-foreground">
                          {delivery.quantity} {delivery.unit}
                        </p>
                      </div>
                      <span className={`px-2 py-1 rounded text-xs ${
                        delivery.status === 'received' ? 'bg-green-100 text-green-700' :
                        delivery.status === 'in_transit' ? 'bg-blue-100 text-blue-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {t(`purchasing.${delivery.status}`)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Notes */}
            {po.notes && (
              <div>
                <h3 className="text-sm font-medium mb-2">{t('common.notes')}</h3>
                <p className="text-sm text-muted-foreground">{po.notes}</p>
              </div>
            )}

            {po.internalNotes && (
              <div>
                <h3 className="text-sm font-medium mb-2">{t('purchasing.internalNotes')}</h3>
                <p className="text-sm text-muted-foreground">{po.internalNotes}</p>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="border-t p-4 flex justify-between">
          <button
            onClick={() => window.print()}
            className="px-4 py-2 rounded-lg border hover:bg-secondary transition-colors flex items-center gap-2"
          >
            <FileDown className="h-4 w-4" />
            {t('common.print')}
          </button>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 rounded-lg border hover:bg-secondary transition-colors"
            >
              {t('common.close')}
            </button>
            {canSend && (
              <button
                onClick={handleSendPO}
                disabled={sendPO.isPending}
                className="px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors flex items-center gap-2 disabled:opacity-50"
              >
                <Send className="h-4 w-4" />
                {sendPO.isPending ? t('common.sending') : t('purchasing.sendPO')}
              </button>
            )}
            {canReceive && (
              <button
                onClick={onRecordGRN}
                className="px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors flex items-center gap-2"
              >
                <Package className="h-4 w-4" />
                {t('purchasing.recordGRN')}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
